import React, { useReducer, useEffect, useCallback, useMemo, useState } from 'react';
import { useApi } from '../../apiHelpers';
import { useToast } from '../../components/Toast/ToastContext';
import useScannerInput from '../hooks/useScannerInput';
import ComponentCard, { ModelSlot, SlotKey, ScannedComponent } from './ComponentCard';
// Shop-floor scanner debug log (047).
import { logScannerDiagnostic } from '../diagnostics';
// itemLabel degrades on its own: display_name, then base_name, then the
// description, then the SKU. So this reads as a name wherever the lookup
// payload carries one and as the code where it does not.
import { itemLabel } from '../../components/ItemName';
import {
  useWorkOrdersApi,
  workOrderErrorCode,
  workOrderErrorMessage,
  type DroneWorkOrderUnit,
  type LiveWorkOrderUnit,
  type FloorRun,
  type UnitCheck,
} from '../../WorkOrders/workOrdersApi';
import { useBomsApi } from '../../Parts/bomsApi';
import {
  DISPLACED_CHOICES,
  conflictSentence,
  postAssemblyCommit,
  type Conflict,
  type DisplacedTo,
} from './commitFit';
import {
  BuiltLanding,
  FinalCheck,
  FlagKey,
  FlagSheet,
  KEY_TONAL,
  ModeChoice,
  RepairModelChoice,
  RequestedLanding,
  RunChoice,
  RunStrip,
  type RepairModel,
} from './WorkOrderStation';

/**
 * Assembly: fit a model's components to one drone, in any order. The slots are
 * the model's parts list (`GET /scanner/slots`), never a list kept here. The one
 * exception is the "This will go away" mode's five (LEGACY_SLOTS), which leave
 * with it.
 *
 * SLOT FILL IS DATA, NOT PHASE. `phase` covers only the submit lifecycle, and
 * which slots are filled is just a map keyed by item kind.
 *
 * THE OPERATOR'S SLOT CHOICE IS AUTHORITATIVE FOR AN UNKNOWN BOARD. A tracked
 * board carries its type from the server and needs no slot. An untracked one has
 * no type anywhere, and UID LENGTH CANNOT SUPPLY IT: ESC moved to a 24-hex STM32
 * UID in March 2026 and gen-2.1 autopilots are also 24 hex. The old code read
 * `length === 12 ? 'AP' : 'ESC'` and filed every gen-2.1 autopilot as an ESC.
 * The server refuses a mismatch too; this is the near half of the same guard.
 */

const DRONE_SERIAL_RE = /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/i;
const BOARD_UID_RE = /^[A-Fa-f0-9]{12,24}$/;
// `<station>:<uid>` off a fireflasher label. Matches the SHAPE, never a list of
// prefixes: enumerating them is what made every AP_UBLOX sticker unreadable.
const BOARD_STICKER_RE = /^[A-Za-z][A-Za-z0-9_-]*:[A-Fa-f0-9]{12,24}$/;
// A part-instance sticker: printBoxInstanceQRLabels encodes the UUID dash-stripped.
const INSTANCE_RE = /^[0-9a-fA-F]{32}$|^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * "This will go away": the pre-work-order bench, kept while the shop switches
 * over and removed with it. Five fixed slots and a commit with no run and no
 * model. Any part on its own saves: a partial build, like the other modes.
 */
const LEGACY_SLOTS: ModelSlot[] = [
  { key: 'AP', label: 'Autopilot', source: 'board', required: true, alternatives: [] },
  { key: 'ESC', label: 'ESC', source: 'board', required: true, alternatives: [] },
  { key: 'LED', label: 'LED', source: 'instance', required: false, alternatives: [] },
  { key: 'PYR', label: 'Pyro', source: 'instance', required: false, alternatives: [] },
  { key: 'GPS', label: 'GPS', source: 'instance', required: false, alternatives: [] },
];
const ALL_SLOTS: SlotKey[] = LEGACY_SLOTS.map((s) => s.key);

/** The legacy flow's slot for a purchased part: segment 1 of its board SKU, one of the five or none. */
const slotFromSku = (sku: string): SlotKey | null => {
  const segment = (sku || '').split('-')[1];
  return ALL_SLOTS.includes(segment) ? segment : null;
};

const scanValidator = (v: string) =>
  DRONE_SERIAL_RE.test(v) || BOARD_UID_RE.test(v) || BOARD_STICKER_RE.test(v) || INSTANCE_RE.test(v);

/**
 * A part's kind read off its SKU, only when the lookup sent no `kind_key`: a
 * board SKU carries it in segment 1 (`FF-PYR-02-1-CH`), a short-kind SKU in
 * segment 0 (`GMT-7Q2K`). Matched against this model's slots only, so a segment
 * that is not one of them names nothing. The server resolves the kind again at
 * commit and is the authority.
 */
const kindFromSku = (sku: string | null | undefined, keys: SlotKey[]): SlotKey | null => {
  const parts = (sku || '').split('-');
  return [parts[1], parts[0]].find((seg) => !!seg && keys.includes(seg)) || null;
};

type Phase = 'SCANNING' | 'SUBMITTING' | 'CONFLICT' | 'SUCCESS';

interface State {
  phase: Phase;
  components: Partial<Record<SlotKey, ScannedComponent>>;
  droneSerial: string | null;
  /** Set only when the operator supplied the serial, so the commit body carries it. */
  pendingDroneSerial: string | null;
  error: string | null;
  conflicts: Conflict[] | null;
}

const initialState: State = {
  phase: 'SCANNING',
  components: {},
  droneSerial: null,
  pendingDroneSerial: null,
  error: null,
  conflicts: null,
};

type Action =
  | { type: 'PLACE'; component: ScannedComponent }
  | { type: 'LOAD_FITTED'; fitted: Partial<Record<SlotKey, ScannedComponent>> }
  | { type: 'CLEAR_SLOT'; slot: SlotKey }
  | { type: 'SET_DRONE'; serial: string; explicit: boolean }
  | { type: 'SUBMIT' }
  | { type: 'SUCCESS'; serial: string }
  | { type: 'COMMITTED' }
  | { type: 'CONFLICT'; message: string; conflicts: Conflict[] }
  | { type: 'ERROR'; message: string }
  | { type: 'RESET' };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'PLACE': {
      const next = { ...state.components, [action.component.slot]: action.component };
      // An AP that already knows its drone answers the question for everyone,
      // unless the operator scanned the drone: that one is where the commit goes.
      const serial = action.component.slot === 'AP' && action.component.droneSerial && !state.pendingDroneSerial
        ? action.component.droneSerial
        : state.droneSerial;
      return { ...state, components: next, droneSerial: serial, error: null };
    }

    // What the drone already carries, merged UNDER anything just scanned: a
    // component in the operator's hand outranks the server's idea of the build,
    // because they may be replacing exactly that part.
    case 'LOAD_FITTED': {
      const next = { ...state.components };
      for (const [slot, component] of Object.entries(action.fitted)) {
        if (!next[slot as SlotKey]) next[slot as SlotKey] = component;
      }
      return { ...state, components: next };
    }

    case 'CLEAR_SLOT': {
      const next = { ...state.components };
      delete next[action.slot];
      // Clearing the AP releases the drone identity it supplied, unless the
      // operator scanned the drone themselves.
      const serial = action.slot === 'AP' && !state.pendingDroneSerial ? null : state.droneSerial;
      return { ...state, components: next, droneSerial: serial, error: null };
    }

    case 'SET_DRONE':
      return {
        ...state,
        droneSerial: action.serial,
        pendingDroneSerial: action.explicit ? action.serial : state.pendingDroneSerial,
        error: null,
      };

    case 'SUBMIT':
      return { ...state, phase: 'SUBMITTING', error: null };

    case 'SUCCESS':
      return { ...state, phase: 'SUCCESS', droneSerial: action.serial, error: null };

    // Committed on the way to the final check: the drone stays on screen, and
    // everything just scanned is now part of its build.
    case 'COMMITTED': {
      const next: Partial<Record<SlotKey, ScannedComponent>> = {};
      for (const [slot, c] of Object.entries(state.components)) {
        next[slot as SlotKey] = { ...c!, preexisting: true, untracked: false };
      }
      return { ...state, phase: 'SCANNING', components: next, error: null };
    }

    case 'CONFLICT':
      return { ...state, phase: 'CONFLICT', error: action.message, conflicts: action.conflicts };

    case 'ERROR':
      return {
        ...state,
        phase: state.phase === 'SUBMITTING' ? 'SCANNING' : state.phase,
        error: action.message,
      };

    case 'RESET':
      return { ...initialState };

    default:
      return state;
  }
}

type Mode = 'work_order' | 'repair' | 'legacy';

/** What fills the screen under the run strip. The reducer's phase still owns the commit. */
type View = 'slots' | 'check' | 'flag' | 'built' | 'requested';

interface Props {
  onPairComplete?: (serial: string) => void;
  operatorId?: string;
  allowUntrackedBoards?: boolean;
}

const AssemblyWorkflow = ({ onPairComplete, operatorId, allowUntrackedBoards }: Props) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [activeSlot, setActiveSlot] = useState<SlotKey | null>(null);
  // Only a slot the operator tapped, never the auto-aim: it is where a part of
  // unknown kind goes, so the first empty slot must not claim it by default.
  const [tappedSlot, setTappedSlot] = useState<SlotKey | null>(null);
  const [serialInput, setSerialInput] = useState('');
  // The session's choice lives outside the reducer: the reducer is one drone's
  // build, and the run outlives every drone scanned for it.
  const [mode, setMode] = useState<Mode | null>(null);
  const [run, setRun] = useState<FloorRun | null>(null);
  const [choosingRun, setChoosingRun] = useState(false);
  const [floor, setFloor] = useState<FloorRun[] | null>(null);
  const [floorError, setFloorError] = useState<string | null>(null);
  const [repairModel, setRepairModel] = useState<RepairModel | null>(null);
  const [choosingModel, setChoosingModel] = useState(false);
  const [models, setModels] = useState<RepairModel[] | null>(null);
  const [modelsError, setModelsError] = useState<string | null>(null);
  // The bench's layout for the chosen run or model. null while it loads.
  const [slots, setSlots] = useState<ModelSlot[] | null>(null);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [slotsReload, setSlotsReload] = useState(0);
  const [view, setView] = useState<View>('slots');
  // undefined while the drone's build is loading, null when it has no unit.
  const [workOrderUnit, setWorkOrderUnit] = useState<DroneWorkOrderUnit | null | undefined>(undefined);
  const [liveUnit, setLiveUnit] = useState<LiveWorkOrderUnit | null>(null);
  const [unitId, setUnitId] = useState<string | null>(null);
  const [check, setCheck] = useState<UnitCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState<'built' | 'request' | null>(null);
  const [flagging, setFlagging] = useState(false);
  const [displacedTo, setDisplacedTo] = useState<DisplacedTo>('scrap');
  // Every replace prompt opens on the default: a choice never carries to the next part.
  useEffect(() => {
    if (state.phase === 'CONFLICT') setDisplacedTo('scrap');
  }, [state.phase]);
  // Which way the last commit was headed, so a forced retry after a conflict keeps going there.
  const [intent, setIntent] = useState<'confirm' | 'check'>('confirm');
  // Slots the last commit claimed for this run that another run had already consumed.
  const [chargedElsewhere, setChargedElsewhere] = useState<SlotKey[]>([]);
  const api = useApi();
  const woApi = useWorkOrdersApi();
  const bomsApi = useBomsApi();
  const toast = useToast();

  const forRun = mode === 'work_order' && !!run;
  const forModel = mode === 'repair' && !!repairModel;
  const legacy = mode === 'legacy';
  const modelCode = forRun ? run!.model_code : forModel ? repairModel!.model_code : '';
  const slotQuery = forRun
    ? `work_order_id=${encodeURIComponent(run!.id)}`
    : forModel
      ? `model_code=${encodeURIComponent(repairModel!.model_code)}`
      : '';
  const visibleSlots = useMemo<SlotKey[]>(() => (slots || []).map((s) => s.key), [slots]);
  const requiredShown = useMemo(() => (slots || []).filter((s) => s.required).map((s) => s.key), [slots]);
  const optionalShown = useMemo(() => (slots || []).filter((s) => !s.required).map((s) => s.key), [slots]);
  const labelOf = useCallback(
    (key: string) => (slots || []).find((s) => s.key === key)?.label || key,
    [slots],
  );
  const onThisRun = forRun && (!!unitId || workOrderUnit?.work_order_id === run!.id);
  const onOtherRun = forRun && !!workOrderUnit && workOrderUnit.work_order_id !== run!.id;

  const scannerDisabled = state.phase !== 'SCANNING' || mode === null || choosingRun || choosingModel
    || !slots || view === 'flag';
  const hasAp = !!state.components.AP;
  const hasFresh = visibleSlots.some((s) => state.components[s] && !state.components[s]!.preexisting);
  // The legacy bench has no "Scan the next drone" cue, so a part with no
  // autopilot to name its drone asks for one here.
  const needsSerial = !state.droneSerial && (hasAp || (legacy && hasFresh));
  // Information, not a gate: assembly spans stations, and the final check is
  // what holds a drone to its whole parts list.
  const stillToFit = useMemo(() => {
    const filled = (key: SlotKey) => !!state.components[key]
      || !!(slots || []).find((s) => s.key === key)?.alternatives?.some((a) => !!state.components[a]);
    const listed: SlotKey[] = [];
    for (const key of requiredShown) {
      if (filled(key)) continue;
      // MOD and RPI empty together are one thing to fit, not two.
      const alts = (slots || []).find((s) => s.key === key)?.alternatives || [];
      if (listed.some((l) => alts.includes(l))) continue;
      listed.push(key);
    }
    return listed;
  }, [requiredShown, slots, state.components]);
  // The legacy commit refuses a drone whose run still owns its parts, so the bench says so first.
  const legacyRefused = legacy && !!liveUnit;
  const canConfirm = !!slots && hasFresh && !!state.droneSerial && state.phase === 'SCANNING' && !legacyRefused;
  // A core-hex unit has no serial, so the unit id alone is what makes it flaggable.
  const flagIdentity = state.droneSerial || check?.unit?.core_hex || (unitId ? 'this drone' : null);

  /** Back to an empty bench, keeping the session's mode and run. */
  const resetDrone = useCallback(() => {
    dispatch({ type: 'RESET' });
    setSerialInput('');
    setView('slots');
    setCheck(null);
    setUnitId(null);
    setTappedSlot(null);
    setChargedElsewhere([]);
  }, []);

  const linkDrone = useCallback(async (serial: string) => {
    try {
      const data = await api.get(`/scanner/drone/${serial}`, { silent: true });
      if (!data?.drone) {
        toast.error('Drone not found');
        return false;
      }
      dispatch({ type: 'SET_DRONE', serial, explicit: true });
      toast.success(`Drone ${serial}`);
      return true;
    } catch (err: any) {
      toast.error(err?.status === 404 ? 'Drone not found' : 'Could not look up that drone');
      return false;
    }
  }, [api, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Which slot a looked-up part goes in. A kind the server reported is final; a
   * kind guessed off the SKU or board type counts only when this model has that
   * slot; otherwise the slot the operator tapped carries it, and the commit
   * checks it against what the part really is.
   */
  const resolveSlot = useCallback((reported: string | null, guessed: string | null, what: string): SlotKey | null => {
    if (reported) {
      if (visibleSlots.includes(reported)) return reported;
      toast.error(`${what} is not on the ${modelCode} parts list`);
      return null;
    }
    if (guessed && visibleSlots.includes(guessed)) return guessed;
    if (tappedSlot) return tappedSlot;
    toast.error(`${what} has no slot for ${modelCode}. Tap the slot it goes in, then scan it again.`);
    return null;
  }, [visibleSlots, tappedSlot, modelCode, toast]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleScan = useCallback(async (raw: string) => {
    const value = raw.trim();

    // A scan after a drone is finished or set aside starts the next one; a scan
    // during the final check is the missing part, so the slots come back.
    if (view === 'built' || view === 'requested') resetDrone();
    else if (view === 'check') setView('slots');

    if (DRONE_SERIAL_RE.test(value)) {
      await linkDrone(value.toUpperCase());
      return;
    }

    try {
      const res = await api.post('/scanner/lookup', { body: { raw: value }, silent: true });

      if (res.kind === 'board') {
        const board = res.payload;
        if (legacy && !ALL_SLOTS.includes(board.board_type)) {
          toast.error(`A ${board.board_type} board is not part of assembly`);
          return;
        }
        const slot = legacy ? (board.board_type as SlotKey) : resolveSlot(
          board.kind_key || null,
          board.board_type || null,
          `${board.kind_key || board.board_type || 'This'} board`,
        );
        if (!slot) return;
        setTappedSlot(null);
        dispatch({
          type: 'PLACE',
          component: {
            slot,
            source: 'board',
            value: board.primary_uid,
            label: board.primary_uid,
            droneSerial: board.serialnumber || null,
          },
        });
        const namesDrone = slot === 'AP' && !state.pendingDroneSerial;
        if (board.serialnumber && state.droneSerial && board.serialnumber !== state.droneSerial && !namesDrone) {
          toast.warning(`Already fitted to ${board.serialnumber}`);
        } else {
          toast.success(board.serialnumber ? `${labelOf(slot)}: ${board.serialnumber}` : `${labelOf(slot)} identified`);
        }
        return;
      }

      if (res.kind === 'part_instance') {
        const instance = res.payload;
        const slot = legacy
          ? slotFromSku(instance.sku)
          : resolveSlot(instance.kind_key || null, kindFromSku(instance.sku, visibleSlots), itemLabel(instance));
        if (!slot) {
          if (legacy) toast.error(`${itemLabel(instance)} is not an assembly component`);
          return;
        }
        setTappedSlot(null);
        if (instance.installed_on && instance.installed_on !== state.droneSerial) {
          toast.warning(`Already fitted to ${instance.installed_on}`);
        }
        dispatch({
          type: 'PLACE',
          component: {
            slot,
            source: 'instance',
            value: instance.id,
            label: instance.sku,
            droneSerial: instance.installed_on || null,
          },
        });
        toast.success(`${labelOf(slot)} identified`);
        return;
      }

      logScannerDiagnostic({
        surface: 'assembly', event: 'unroutable_kind',
        raw_value: value, detail: { kind: res?.kind },
      });
      toast.error('That scan is not a component or a drone');
    } catch (err: any) {
      if (err?.status !== 404) {
        toast.error('Could not look up that scan');
        return;
      }

      // Unknown to the database. Only a board can be created on the spot, and
      // only into a slot the operator has named.
      logScannerDiagnostic({
        surface: 'assembly', event: 'lookup_miss',
        raw_value: value, detail: { allow_untracked: !!allowUntrackedBoards, active_slot: activeSlot },
      });
      if (!allowUntrackedBoards) {
        toast.error('Not found. Has it been flashed or received?');
        return;
      }
      const uid = value.includes(':') ? value.split(':').pop()! : value;
      if (!BOARD_UID_RE.test(uid)) {
        toast.error('Not found. A part sticker cannot be created here.');
        return;
      }
      const aimed = (slots || []).find((s) => s.key === activeSlot);
      if (!aimed || aimed.source === 'instance') {
        toast.error('Unknown board. Tap the slot it goes in to say which it is.');
        return;
      }
      setTappedSlot(null);
      dispatch({
        type: 'PLACE',
        component: {
          slot: aimed.key,
          source: 'board',
          value: uid.toUpperCase(),
          label: uid.toUpperCase(),
          droneSerial: null,
          untracked: true,
        },
      });
      toast.warning(`New ${aimed.label} board`);
    }
  }, [api, toast, activeSlot, allowUntrackedBoards, state.droneSerial, state.pendingDroneSerial, linkDrone, view, resetDrone, resolveSlot, labelOf, slots, visibleSlots, legacy]); // eslint-disable-line react-hooks/exhaustive-deps

  const { inputRef, focusInput } = useScannerInput({
    onScan: handleScan,
    surface: 'assembly',
    minLength: 12,
    validator: scanValidator,
    disabled: scannerDisabled,
  });

  useEffect(() => {
    if (activeSlot && !scannerDisabled) focusInput();
  }, [activeSlot, scannerDisabled, focusInput]);

  // Once the drone is known, show what it already carries. Assembly is
  // incremental: a drone gets its AP and ESC at one station and its pyro days
  // later, and the operator needs to see the build they are adding to rather
  // than re-presenting boards that never left the drone.
  //
  // Keyed on the serial alone. Keying on `components` too would re-fetch on
  // every scan and fight the operator's own input.
  useEffect(() => {
    setWorkOrderUnit(undefined);
    setLiveUnit(null);
    setUnitId(null);
    setCheck(null);
    if (!state.droneSerial) return;
    let cancelled = false;

    api.get(`/scanner/assembly/${state.droneSerial}`, { silent: true })
      .then((data: any) => {
        if (cancelled) return;
        setWorkOrderUnit(data?.work_order_unit || null);
        setLiveUnit(data?.live_work_order_unit || null);
        if (!data?.fitted) return;
        const fitted: Partial<Record<SlotKey, ScannedComponent>> = {};
        for (const [slot, c] of Object.entries<any>(data.fitted)) {
          fitted[slot as SlotKey] = {
            slot: slot as SlotKey,
            source: c.source,
            value: c.value,
            label: c.label,
            droneSerial: state.droneSerial,
            preexisting: true,
          };
        }
        dispatch({ type: 'LOAD_FITTED', fitted });
      })
      .catch(() => {
        // A build we cannot read is not a reason to block scanning.
        if (!cancelled) setWorkOrderUnit(null);
      });

    return () => { cancelled = true; };
  }, [state.droneSerial]); // eslint-disable-line react-hooks/exhaustive-deps

  // The bench's slots, from the run's parts list or the repair model's, or the
  // legacy five with no read at all. Keyed on the query, so a new drone on the
  // same run does not re-read them.
  useEffect(() => {
    setSlots(legacy ? LEGACY_SLOTS : null);
    setSlotsError(null);
    if (!slotQuery) return;
    let cancelled = false;
    api.get(`/scanner/slots?${slotQuery}`, { silent: true })
      .then((rows: ModelSlot[] | null) => {
        if (!cancelled) setSlots(Array.isArray(rows) ? rows : []);
      })
      .catch((err: any) => {
        if (!cancelled) setSlotsError(workOrderErrorMessage(err, 'The slots for this model did not load.'));
      });
    return () => { cancelled = true; };
  }, [slotQuery, slotsReload, legacy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aim at the first empty slot the model uses, so the common path needs no tapping.
  useEffect(() => {
    if (state.phase !== 'SCANNING') { setActiveSlot(null); return; }
    setActiveSlot((prev) => {
      if (prev && !state.components[prev] && visibleSlots.includes(prev)) return prev;
      return visibleSlots.find((s) => !state.components[s]) || null;
    });
  }, [state.components, state.phase, visibleSlots]);

  useEffect(() => {
    if (state.phase !== 'SUCCESS') return;
    // Local, not a ref: StrictMode double-invokes and a ref would race.
    const timer = setTimeout(resetDrone, 3000);
    return () => clearTimeout(timer);
  }, [state.phase, resetDrone]);

  useEffect(() => {
    if (view !== 'built') return;
    const timer = setTimeout(resetDrone, 2500);
    return () => clearTimeout(timer);
  }, [view, resetDrone]);

  const loadFloor = async () => {
    setFloor(null);
    setFloorError(null);
    try {
      setFloor((await woApi.floor()) || []);
    } catch (err) {
      setFloorError(workOrderErrorMessage(err, 'The runs on the floor did not load.'));
    }
  };

  const chooseWorkOrder = () => {
    setMode('work_order');
    setChoosingRun(true);
    loadFloor();
  };

  const loadModels = async () => {
    setModels(null);
    setModelsError(null);
    try {
      const all = (await bomsApi.listProducts()) || [];
      // The bench fits drones by serial; a charger has no serial, so only products with a model code are offered.
      setModels(all.filter((m): m is typeof m & { model_code: string } => !!m.bom_id && !!m.model_code));
    } catch (err) {
      setModelsError(workOrderErrorMessage(err, 'The drone models did not load.'));
    }
  };

  const chooseRepair = () => {
    setMode('repair');
    setRun(null);
    setChoosingRun(false);
    setRepairModel(null);
    setChoosingModel(true);
    loadModels();
    resetDrone();
  };

  const chooseLegacy = () => {
    setMode('legacy');
    setRun(null);
    setChoosingRun(false);
    setRepairModel(null);
    setChoosingModel(false);
    resetDrone();
  };

  const pickModel = (m: RepairModel) => {
    setRepairModel(m);
    setChoosingModel(false);
    resetDrone();
  };

  const pickRun = (r: FloorRun) => {
    setRun(r);
    setChoosingRun(false);
    resetDrone();
  };

  const changeRun = () => {
    resetDrone();
    if (legacy) {
      setMode(null);
    } else if (mode === 'work_order') {
      setChoosingRun(true);
      loadFloor();
    } else {
      setChoosingModel(true);
      loadModels();
    }
  };

  const loadCheck = async (serial: string | null = state.droneSerial) => {
    if (!run || !serial) return;
    setChecking(true);
    try {
      const c = await woApi.check(run.id, serial);
      setCheck(c);
      if (c?.unit?.id) setUnitId(c.unit.id);
      setView('check');
    } catch (err) {
      toast.error(workOrderErrorMessage(err, 'The check did not load. Try again.'));
    } finally {
      setChecking(false);
    }
  };

  const submit = async (force = false, headed: 'confirm' | 'check' = 'confirm') => {
    setIntent(headed);
    dispatch({ type: 'SUBMIT' });

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30000);

    try {
      // Only what the operator actually scanned. The server skips anything
      // already fitted anyway, but sending it would make the request lie about
      // what happened at the bench.
      const fresh = visibleSlots.filter((s) => state.components[s] && !state.components[s]!.preexisting);
      // The exception: a drone built at earlier stations joins this run with
      // nothing new in hand, and a commit needs a component. Its fitted parts
      // go up as the bind, and the server records them as unchanged.
      const sent = fresh.length || !forRun || onThisRun
        ? fresh
        : visibleSlots.filter((s) => state.components[s]);
      const body: Record<string, unknown> = {
        components: sent.map((s) => ({ slot: s, value: state.components[s]!.value })),
      };
      if (state.pendingDroneSerial) body.drone_serial = state.pendingDroneSerial;
      if (forRun) body.work_order_id = run!.id;
      if (forModel) body.model_code = repairModel!.model_code;
      if (operatorId) body.operator_id = operatorId;
      if (allowUntrackedBoards) body.allow_untracked = true;
      if (force) {
        body.force = true;
        // Only a slot that already held a part has an old part to place.
        if (state.conflicts?.some((c) => c.type === 'slot_occupied')) body.displaced_to = displacedTo;
      }

      const data = await postAssemblyCommit(api, body, controller.signal);

      if (data?.work_order?.unit?.id) setUnitId(data.work_order.unit.id);
      setChargedElsewhere(
        (Array.isArray(data?.consumed) ? data.consumed : [])
          .filter((c: any) => c?.reason === 'already' && c?.claimed === true && c?.slot)
          .map((c: any) => c.slot as SlotKey),
      );
      onPairComplete?.(data.drone.serialnumber);
      if (headed === 'check') {
        dispatch({ type: 'COMMITTED' });
        await loadCheck(data.drone.serialnumber);
      } else {
        dispatch({ type: 'SUCCESS', serial: data.drone.serialnumber });
      }
    } catch (err: any) {
      const conflicts = err?.data?.conflicts || (err?.data?.conflict ? [err.data.conflict] : []);
      // A run's refusal (wrong model, on another run, run full) is a 409 too,
      // but there is nothing to move, so it must not offer "Move it here".
      if (err?.status === 409 && conflicts.length > 0) {
        dispatch({ type: 'CONFLICT', message: err.message || 'Conflict', conflicts });
        return;
      }
      const msg = err?.name === 'AbortError'
        ? 'Timed out'
        : err?.status === 409 ? workOrderErrorMessage(err, 'Assembly failed') : err?.message || 'Assembly failed';
      logScannerDiagnostic({
        surface: 'assembly', event: 'commit_error',
        detail: { status: err?.status, message: msg, slots: Object.keys(state.components) },
      });
      dispatch({ type: 'ERROR', message: msg });
      toast.error(msg);
    } finally {
      clearTimeout(timeout);
    }
  };

  /** Commit first when the bench holds something unsaved or the drone is not on the run yet. */
  const finalCheck = async () => {
    if (!run || !state.droneSerial || checking) return;
    const fresh = visibleSlots.some((s) => state.components[s] && !state.components[s]!.preexisting);
    if (fresh || !onThisRun) {
      await submit(false, 'check');
      return;
    }
    await loadCheck();
  };

  const markBuilt = async () => {
    if (!run || !check?.unit?.id || busy) return;
    setBusy('built');
    try {
      await woApi.completeOne(run.id, check.unit.id, 'scan', operatorId);
      setRun((r) => (r ? { ...r, completed_count: r.completed_count + 1 } : r));
      setView('built');
    } catch (err) {
      const code = workOrderErrorCode(err);
      if (code === 'traced_missing' || code === 'parts_short') {
        // Something changed since the check: read it again so the screen says what.
        toast.warning(workOrderErrorMessage(err));
        await loadCheck();
      } else {
        toast.error(workOrderErrorMessage(err, 'The drone was not marked built. Try again.'));
      }
    } finally {
      setBusy(null);
    }
  };

  const requestBox = async () => {
    if (!run || !check || busy) return;
    setBusy('request');
    try {
      await woApi.requestPicks(run.id, {
        unit_id: check.unit?.id,
        lines: [
          ...check.short.map((s) => ({ line_id: s.line_id, quantity: Math.max(1, s.quantity ?? s.need - s.in_assembly) })),
          ...check.traced
            .filter((t) => !t.fitted && t.in_assembly === 0)
            .map((t) => ({ line_id: t.line_id, quantity: Math.max(1, t.need ?? 1) })),
        ],
        ...(operatorId ? { operator_id: operatorId } : {}),
      });
      setRun((r) => (r ? { ...r, open_pick_requests: Math.max(1, r.open_pick_requests || 0) } : r));
      setView('requested');
    } catch (err) {
      toast.error(workOrderErrorMessage(err, 'The request did not go through. Try again.'));
    } finally {
      setBusy(null);
    }
  };

  const flagDrone = async (reason: string) => {
    const serial = state.droneSerial;
    if (!run || !flagIdentity || flagging) return;
    setFlagging(true);
    try {
      // A drone bound at an earlier station has no unit id on this device yet.
      const id = unitId ?? (serial ? (await woApi.check(run.id, serial))?.unit?.id : null);
      if (!id) {
        toast.error('That drone is not on this run yet, so there is nothing to flag.');
        return;
      }
      await woApi.flag(run.id, id, reason, operatorId);
      toast.success(`Flagged ${flagIdentity}. Set it aside for repair.`);
      resetDrone();
    } catch (err) {
      toast.error(workOrderErrorMessage(err, 'The drone was not flagged. Try again.'));
    } finally {
      setFlagging(false);
    }
  };

  const handleSerialSubmit = async () => {
    const value = serialInput.trim().toUpperCase();
    if (!DRONE_SERIAL_RE.test(value)) {
      toast.error('Serials look like XXXX-XXXX-XXXX-XXXX');
      return;
    }
    if (await linkDrone(value)) setSerialInput('');
  };

  if (mode === null) {
    return <ModeChoice onWorkOrder={chooseWorkOrder} onRepair={chooseRepair} onLegacy={chooseLegacy} />;
  }

  if (choosingModel) {
    return (
      <RepairModelChoice
        models={models}
        error={modelsError}
        onRetry={loadModels}
        onPick={pickModel}
        onBack={() => {
          setChoosingModel(false);
          setRepairModel(null);
          setMode(null);
        }}
      />
    );
  }

  if (choosingRun) {
    return (
      <RunChoice
        runs={floor}
        error={floorError}
        onRetry={loadFloor}
        onPick={pickRun}
        onBack={() => {
          setChoosingRun(false);
          setRun(null);
          setMode(null);
        }}
        onRepair={chooseRepair}
      />
    );
  }

  if (state.phase === 'SUCCESS') {
    return (
      <div className="flex flex-col items-center justify-center px-6 py-16 gap-3">
        <svg className="w-16 h-16 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-green-300 text-lg font-semibold">Assembly recorded</p>
        <p className="text-green-200 font-mono tabular-nums text-sm">{state.droneSerial}</p>
        {chargedElsewhere.map((slot) => (
          <p key={slot} className="text-center text-xs text-on-surface-variant">
            The {labelOf(slot)} was already charged to another drone's run
          </p>
        ))}
      </div>
    );
  }

  const optionalCols = optionalShown.length >= 3 ? 'grid-cols-3' : optionalShown.length === 2 ? 'grid-cols-2' : 'grid-cols-1';

  let body: React.ReactNode;
  if (view === 'built' && run) {
    body = <BuiltLanding serial={state.droneSerial || ''} run={run} onNext={resetDrone} />;
  } else if (view === 'requested') {
    body = <RequestedLanding serial={state.droneSerial || ''} onNext={resetDrone} />;
  } else if (view === 'flag' && flagIdentity) {
    body = (
      <FlagSheet
        identity={flagIdentity}
        busy={flagging}
        onSubmit={flagDrone}
        onCancel={() => setView(check ? 'check' : 'slots')}
      />
    );
  } else if (view === 'check' && check && state.droneSerial) {
    body = (
      <>
        {chargedElsewhere.map((slot) => (
          <p key={slot} className="text-xs text-on-surface-variant">
            The {labelOf(slot)} was already charged to another drone's run
          </p>
        ))}
        <FinalCheck
          check={check}
          slotLabel={labelOf}
          serial={state.droneSerial}
          busy={busy}
          onBuilt={markBuilt}
          onRequestBox={requestBox}
          onBack={() => setView('slots')}
        />
        <div className="flex justify-end">
          <FlagKey onClick={() => setView('flag')} />
        </div>
      </>
    );
  } else {
    body = (
      <>
        {/* The drone this becomes. Everything else on screen attaches to it, so it
            is the anchor rather than a footnote, and it shows the shape it is
            waiting for before the autopilot supplies it. */}
        <div className={`rounded-xl border px-4 py-3 text-center transition-colors ${
          state.droneSerial ? 'border-green-600 bg-green-900/25' : 'border-gray-700 border-dashed bg-transparent'
        }`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-on-surface-variant">
            {forRun && !state.droneSerial ? 'Scan the next drone' : 'Drone'}
          </div>
          <div className={`font-mono tabular-nums text-lg tracking-tight ${
            state.droneSerial ? 'text-green-200' : 'text-gray-700'
          }`}>
            {state.droneSerial || 'XXXX-XXXX-XXXX-XXXX'}
          </div>
        </div>

        {forRun && state.droneSerial && workOrderUnit !== undefined && (onOtherRun || (workOrderUnit && onThisRun)) && (
          <p className={`text-center text-xs ${onOtherRun ? 'text-orange-300' : 'text-on-surface-variant'}`}>
            {onOtherRun
              ? `This drone is on ${workOrderUnit!.number}, not ${run!.number}.`
              : `Unit ${workOrderUnit!.position} of ${run!.number}`}
          </p>
        )}

        {legacyRefused && state.droneSerial && (
          <p className="text-center text-xs text-orange-300">
            {liveUnit!.flagged
              ? `This drone was flagged off ${liveUnit!.number}. Use Repair to fix it.`
              : `This drone is on ${liveUnit!.number}. Use Work order mode to fit its parts.`}
          </p>
        )}

        {needsSerial && (
          <div className="rounded-lg border border-orange-700 bg-orange-900/25 p-3">
            <p className="text-orange-300 text-xs text-center mb-2">
              {hasAp
                ? 'This autopilot has no drone yet. Scan the drone QR, or type its serial.'
                : 'Which drone is this? Scan the drone QR, or type its serial.'}
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={serialInput}
                onChange={(e) => setSerialInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSerialSubmit()}
                placeholder="XXXX-XXXX-XXXX-XXXX"
                aria-label="Drone serial number"
                autoComplete="off"
                className="flex-1 min-w-0 px-3 py-2 bg-[#1a1a1a] border border-outline rounded text-white text-sm font-mono tabular-nums placeholder:text-on-surface-variant focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <button
                type="button"
                onClick={handleSerialSubmit}
                disabled={!serialInput.trim()}
                className="px-4 py-2 bg-orange-600 active:bg-orange-500 disabled:bg-gray-700 disabled:text-on-surface-disabled text-white font-semibold rounded text-sm"
              >
                Link
              </button>
            </div>
          </div>
        )}

        {slotsError && (
          <div role="alert" className="rounded-xl bg-surface-container-low p-4 text-sm">
            <p className="text-on-surface">{slotsError}</p>
            <button type="button" onClick={() => setSlotsReload((n) => n + 1)} className={`mt-3 ${KEY_TONAL}`}>
              Try again
            </button>
          </div>
        )}

        {!slots && !slotsError && (
          <div className="grid grid-cols-2 gap-3" aria-busy="true" aria-label="Loading the slots for this model">
            {[0, 1].map((i) => (
              <div key={i} className="min-h-[112px] rounded-xl bg-surface-container-low motion-safe:animate-pulse" />
            ))}
          </div>
        )}

        {slots && slots.length === 0 && (
          <p className="rounded-xl bg-surface-container-low p-4 text-sm text-on-surface">
            No part on the {modelCode} parts list is scanned at the bench, so there is nothing to fit here. Mark a line "Scanned at the bench" on its Product BOMs page.
          </p>
        )}

        <div className="grid grid-cols-2 gap-3">
          {requiredShown.map((slot) => (
            <ComponentCard
              key={slot}
              slot={slot}
              label={labelOf(slot)}
              component={state.components[slot] || null}
              scanTarget={activeSlot === slot}
              needsSerial={slot === 'AP' && needsSerial}
              onTap={() => { setActiveSlot(slot); setTappedSlot(slot); }}
            />
          ))}
        </div>

        {optionalShown.length > 0 && (
          <>
            {/* Not a divider for looks: everything above is on the finished drone's
                parts list, everything below may be left off. */}
            <div className="flex items-center gap-3 pt-1" aria-hidden="true">
              <div className="h-px flex-1 bg-gray-800" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-on-surface-variant">Optional</span>
              <div className="h-px flex-1 bg-gray-800" />
            </div>

            <div className={`grid ${optionalCols} gap-2`}>
              {optionalShown.map((slot) => (
                <ComponentCard
                  key={slot}
                  slot={slot}
                  label={labelOf(slot)}
                  component={state.components[slot] || null}
                  size="compact"
                  scanTarget={activeSlot === slot}
                  onTap={() => { setActiveSlot(slot); setTappedSlot(slot); }}
                />
              ))}
            </div>
          </>
        )}

        {state.phase === 'CONFLICT' && (
          <div className="rounded-lg border border-orange-600 bg-orange-900/30 p-3">
            {(state.conflicts || []).length === 1 ? (
              <p className="text-orange-300 text-sm font-semibold mb-3">{conflictSentence(state.conflicts![0], labelOf(state.conflicts![0].slot))}</p>
            ) : (
              <>
                <p className="text-orange-300 text-sm font-semibold mb-1">{state.error}</p>
                <ul className="text-orange-200 text-xs space-y-1 mb-3">
                  {(state.conflicts || []).map((c, i) => (
                    <li key={i}>{conflictSentence(c, labelOf(c.slot))}</li>
                  ))}
                </ul>
              </>
            )}
            {(state.conflicts || []).some((c) => c.type === 'slot_occupied') && (
              <div className="mb-3">
                <p id="displaced-label" className="mb-1 text-xs text-orange-200">What happens to the old part</p>
                <div role="radiogroup" aria-labelledby="displaced-label" className="flex gap-0.5 rounded-lg bg-black/30 p-0.5">
                  {DISPLACED_CHOICES.map((d) => (
                    <span key={d.value} title={d.tip} className="flex flex-1">
                      <button
                        type="button"
                        role="radio"
                        aria-checked={displacedTo === d.value}
                        onClick={() => setDisplacedTo(d.value)}
                        className={`flex-1 min-h-[2.75rem] rounded-md text-sm font-medium ${
                          displacedTo === d.value ? 'bg-orange-500/25 text-orange-100' : 'text-orange-200/70 active:bg-white/5'
                        }`}
                      >
                        {d.label}
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => submit(true, intent)}
                className="flex-1 py-3 bg-orange-600 active:bg-orange-500 text-white font-semibold rounded"
              >
                {(state.conflicts || []).some((c) => c.type === 'slot_occupied') ? 'Replace it' : 'Move it here'}
              </button>
              <button
                type="button"
                onClick={resetDrone}
                className="flex-1 py-3 bg-gray-800 active:bg-gray-700 text-on-surface font-semibold rounded"
              >
                Start over
              </button>
            </div>
          </div>
        )}

        {slots && state.phase === 'SCANNING' && stillToFit.length > 0 && visibleSlots.some((s) => state.components[s]) && (
          <p className="text-center text-xs text-on-surface-variant">
            Still to fit on this drone: {stillToFit.map(labelOf).join(', ')}
          </p>
        )}

        {state.error && state.phase === 'SCANNING' && (
          <p className="text-red-400 text-xs text-center">{state.error}</p>
        )}

        {state.phase !== 'CONFLICT' && (
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => submit(false)}
              disabled={!canConfirm}
              className="flex-1 py-4 bg-green-700 active:bg-green-600 disabled:bg-gray-800 disabled:text-on-surface-disabled text-white font-semibold rounded-lg text-base"
            >
              {state.phase === 'SUBMITTING' && intent === 'confirm' ? 'Recording...' : 'Confirm assembly'}
            </button>
            {Object.keys(state.components).length > 0 && (
              <button
                type="button"
                onClick={resetDrone}
                disabled={state.phase === 'SUBMITTING'}
                className="px-4 py-4 bg-gray-800 active:bg-gray-700 text-on-surface-variant rounded-lg text-sm"
              >
                Clear
              </button>
            )}
          </div>
        )}

        {forRun && state.phase !== 'CONFLICT' && (
          <span title="Check every part is on this drone before the dome goes on" className="flex">
            <button
              type="button"
              onClick={finalCheck}
              disabled={!state.droneSerial || onOtherRun || state.phase !== 'SCANNING' || checking}
              className={KEY_TONAL}
            >
              {checking || (state.phase === 'SUBMITTING' && intent === 'check') ? 'Checking…' : 'Final check'}
            </button>
          </span>
        )}

        {onThisRun && flagIdentity && (
          <div className="flex justify-end">
            <FlagKey onClick={() => setView('flag')} />
          </div>
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-3 px-4 py-4 max-w-sm mx-auto w-full">
      {/* Capture target for a scanner not in HID mode. NO inputMode="none": it
          suppresses InputConnection on these handhelds. See useScannerInput. */}
      <input ref={inputRef} className="opacity-0 absolute h-px w-px" autoComplete="off" aria-hidden="true" tabIndex={-1} />

      <RunStrip run={forRun ? run : null} repairModel={forModel ? repairModel : null} legacy={legacy} onChange={changeRun} />

      {body}
    </div>
  );
};

export default AssemblyWorkflow;
